import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Callout,
    ErrorState,
    FormField,
    FormFieldMessage,
    Input,
    OptionCard,
    OptionCardGroup,
    Select,
    SectionCard,
    Skeleton,
    Tabs,
    type TabItem,
} from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { AppEndpointPathsCard } from './AppEndpointPathsCard';
import { CHECK_ENDPOINT_KEYS, SYNC_ENDPOINT_KEYS } from './endpointPaths';
import { AppProvisioningResultModal } from './AppProvisioningResultModal';
import { useProvisioningResult } from './useProvisioningResult';
import { APP_TYPE_OPTIONS } from '../Apps/useCreateAppForm';
import { AppUriListField } from '../Apps/AppUriListField';
import { useAppDetailPage } from './useAppDetailPage';
import { useEditAppForm } from './useEditAppForm';
import type { AppDetailTabKey } from './types';
import detail from './AppDetail.module.css';
import styles from './AppEditPage.module.css';

const TAB_ORDER: AppDetailTabKey[] = ['overview', 'roles', 'connection', 'audit'];

function ChangedBadge() {
    const { t } = useTranslation();
    return (
        <span className="app-edit-field-badge app-edit-field-badge--changed">
            {t('console.appDetail.edit.changedBadge')}
        </span>
    );
}

function LockedBadge() {
    const { t } = useTranslation();
    return (
        <span className="app-edit-field-badge app-edit-field-badge--locked">
            {t('console.appDetail.edit.lockedBadge')}
        </span>
    );
}

function fieldLabel(base: ReactNode, changed: boolean): ReactNode {
    return (
        <>
            {base}
            {changed && <ChangedBadge />}
        </>
    );
}

/**
 * EAA-1846 (design frame 51 "App edit", `/console/apps/:code/edit` — added
 * to the pack the same day as the "/console/apps/:id thiếu màn sửa" report
 * this closes): edit surface for an existing registry row. Shares the app-
 * detail shell's identity row and 4-tab bar (Tổng quan stays "active" while
 * here), reuses `useCreateAppForm`'s field set via the sibling
 * `useEditAppForm` hook (same fields `AdminAppUpdateSerializer` accepts,
 * `code` excluded — immutable, every RoleMap/OrganizationAppScope/launcher
 * deep-link joins on it).
 *
 * Per the design spec: changed fields carry a blue outline + "đã đổi" badge,
 * the locked code field carries a "không sửa được" badge, and the right
 * column lists every unsaved change as a before/after pair rather than a
 * generic "you have unsaved changes" banner — an operator about to overwrite
 * a live app's config should see exactly what is about to change.
 */
export function AppEditPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const vm = useAppDetailPage();

    function goToDetail(tab?: AppDetailTabKey) {
        navigate(
            tab && tab !== 'overview'
                ? `/console/apps/${vm.code}?tab=${tab}`
                : `/console/apps/${vm.code}`
        );
    }

    // EAA-1709: a save may provision clients (e.g. switching to `hybrid` creates
    // `<code>-api`) and return its secret once, or report a failed step — the
    // page stays until the operator has seen it.
    const provisioningResult = useProvisioningResult(goToDetail);
    const form = useEditAppForm(vm.app, provisioningResult.handleResult);

    useConsoleBreadcrumb([
        { label: t('console.apps.page.breadcrumbParent') },
        { label: t('console.apps.page.title'), to: '/console/apps' },
        ...(vm.app ? [{ label: vm.app.name, to: `/console/apps/${vm.code}` }] : []),
        { label: t('console.appDetail.edit.breadcrumbCurrent') },
    ]);

    // EAA-1846: fail-closed, same convention `AppDetailPage` uses for
    // `canView` — a caller without `console.apps.manage` (the same
    // permission `AdminAppDetailView.patch` requires server-side) must not
    // reach an edit form it cannot submit.
    if (!vm.canView || (vm.appsQuery.isSuccess && vm.app && !vm.canManage)) {
        return null;
    }

    function confirmDiscardIfDirty(): boolean {
        if (form.unsavedCount === 0) return true;
        return window.confirm(t('console.appDetail.edit.discardConfirm'));
    }

    function handleTabClick(tab: AppDetailTabKey) {
        if (tab === 'overview') return;
        if (!confirmDiscardIfDirty()) return;
        goToDetail(tab);
    }

    function handleCancel() {
        if (!confirmDiscardIfDirty()) return;
        goToDetail();
    }

    function fieldError(key: string): string | null {
        return form.fieldErrors[key]?.[0] ?? null;
    }

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit app-detail-page app-edit-page">
            {vm.appsQuery.isLoading && (
                <div role="status" aria-live="polite">
                    <span className="portal-sr-only">{t('common.loading')}</span>
                    <Skeleton height="4rem" />
                </div>
            )}

            {vm.appsQuery.isError && (
                <ErrorState
                    description={t('console.appDetail.loadError')}
                    onRetry={() => vm.appsQuery.refetch()}
                    retrying={vm.appsQuery.isFetching}
                />
            )}

            {vm.appsQuery.isSuccess && !vm.app && (
                <ErrorState description={t('console.appDetail.notFound', { code: vm.code })} />
            )}

            {vm.app && form.loaded && form.scalars && (
                <>
                    <div className={detail.header}>
                        <div className={detail.identity}>
                            <span className={detail.identityTile} aria-hidden="true">
                                {vm.code.slice(0, 3).toUpperCase()}
                            </span>
                            <div className={detail.identityBody}>
                                <div className={detail.identityTitleRow}>
                                    <h1 className={detail.identityName}>{vm.app.name}</h1>
                                    <code className={detail.identityCode}>{vm.app.code}</code>
                                    <span
                                        className={`${detail.identityStatus} ${
                                            vm.app.enabled ? detail.isApproved : detail.isPending
                                        }`}
                                    >
                                        {vm.app.enabled
                                            ? t('console.appDetail.statusApproved')
                                            : t('console.appDetail.statusPending')}
                                    </span>
                                    <span className={styles.editingBadge}>
                                        {t('console.appDetail.edit.editingBadge')}
                                    </span>
                                </div>
                            </div>
                            <div className={`${detail.identityActions} ${styles.headerActions}`}>
                                {form.unsavedCount > 0 && (
                                    <span className={styles.unsavedCount}>
                                        {t('console.appDetail.edit.unsavedCount', {
                                            count: form.unsavedCount,
                                        })}
                                    </span>
                                )}
                                <Button
                                    onClick={handleCancel}
                                    disabled={form.saveMutation.isPending}
                                >
                                    {t('console.appDetail.edit.cancelButton')}
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    form="portal-app-edit-form"
                                    disabled={
                                        !form.canSubmit ||
                                        form.saveMutation.isPending ||
                                        form.unsavedCount === 0
                                    }
                                >
                                    {form.saveMutation.isPending
                                        ? t('console.appDetail.edit.savingLabel')
                                        : t('console.appDetail.edit.saveButton')}
                                </Button>
                            </div>
                        </div>

                        {/* EAA-1804: this was a hand-rolled `role="tablist"` styled by
                            `.app-detail-tab`/`.app-detail-tab--on`, both deleted from
                            24-app-detail.css when `AppDetailHeader.tsx` migrated its own
                            tab bar to the kit `Tabs` (see that file's comment above its
                            own `<Tabs>` usage). These tabs were rendering completely
                            unstyled in production. Fix: use the same kit `Tabs`, mapping
                            `TAB_ORDER` the identical way `AppDetailHeader` does, with
                            `activeKey` fixed to 'overview' since this page IS the overview
                            tab's edit surface. */}
                        <Tabs<AppDetailTabKey>
                            className={detail.tabs}
                            items={TAB_ORDER.map(
                                (key): TabItem<AppDetailTabKey> => ({
                                    key,
                                    label: t(`console.appDetail.tabs.${key}`),
                                })
                            )}
                            activeKey="overview"
                            onChange={handleTabClick}
                        />
                    </div>

                    <div className="app-detail-tab-panel fit-grow">
                        <div className={styles.body}>
                            <form
                                id="portal-app-edit-form"
                                className={styles.bodyMain}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    form.onSubmit();
                                }}
                            >
                                <SectionCard title={t('console.apps.createPage.infoSectionTitle')}>
                                    <div className="portal-admin-form">
                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createPage.nameLabel'),
                                                form.changedKeys.has('name')
                                            )}
                                            error={fieldError('name')}
                                        >
                                            <Input
                                                value={form.scalars.name}
                                                onChange={(e) =>
                                                    form.setScalar('name', e.target.value)
                                                }
                                                required
                                            />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createModal.codeLabel'),
                                                false
                                            )}
                                            hint={t('console.appDetail.edit.codeLockedHint')}
                                        >
                                            <Input
                                                value={vm.app.code}
                                                disabled
                                                className="apps-create-mono-input"
                                            />
                                            <FormFieldMessage hint={<LockedBadge />} />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createModal.appTypeLabel'),
                                                form.changedKeys.has('app_type')
                                            )}
                                        >
                                            <Select
                                                value={form.scalars.app_type}
                                                onChange={(value) =>
                                                    form.setScalar('app_type', value)
                                                }
                                                options={APP_TYPE_OPTIONS.map((option) => ({
                                                    value: option.value,
                                                    label: t(option.labelKey),
                                                }))}
                                            />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createPage.audienceLabel'),
                                                form.changedKeys.has('audience')
                                            )}
                                            error={fieldError('audience')}
                                        >
                                            <Input
                                                value={form.scalars.audience}
                                                onChange={(e) =>
                                                    form.setScalar('audience', e.target.value)
                                                }
                                            />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createPage.webUrlLabel'),
                                                form.changedKeys.has('web_url')
                                            )}
                                            error={form.webUrlError ?? fieldError('web_url')}
                                        >
                                            <Input
                                                value={form.scalars.web_url}
                                                onChange={(e) =>
                                                    form.setScalar('web_url', e.target.value)
                                                }
                                                onBlur={() => form.markTouched('web_url')}
                                            />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createPage.apiBaseLabel'),
                                                form.changedKeys.has('api_base')
                                            )}
                                            error={fieldError('api_base')}
                                        >
                                            <Input
                                                value={form.scalars.api_base}
                                                onChange={(e) =>
                                                    form.setScalar('api_base', e.target.value)
                                                }
                                            />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createModal.iconUrlLabel'),
                                                form.changedKeys.has('icon_url')
                                            )}
                                            error={form.iconUrlError ?? fieldError('icon_url')}
                                        >
                                            <Input
                                                value={form.scalars.icon_url}
                                                onChange={(e) =>
                                                    form.setScalar('icon_url', e.target.value)
                                                }
                                                onBlur={() => form.markTouched('icon_url')}
                                            />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createPage.descriptionLabel'),
                                                form.changedKeys.has('description')
                                            )}
                                            error={fieldError('description')}
                                            className="apps-create-field--full"
                                        >
                                            <Input.TextArea
                                                value={form.scalars.description}
                                                onChange={(e) =>
                                                    form.setScalar('description', e.target.value)
                                                }
                                                rows={2}
                                            />
                                        </FormField>
                                    </div>
                                </SectionCard>

                                <SectionCard title={t('console.apps.createPage.ownerSectionTitle')}>
                                    <div className="portal-admin-form">
                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createPage.ownerNameLabel'),
                                                form.changedKeys.has('owner_name')
                                            )}
                                            error={fieldError('owner_name')}
                                        >
                                            <Input
                                                value={form.scalars.owner_name}
                                                onChange={(e) =>
                                                    form.setScalar('owner_name', e.target.value)
                                                }
                                            />
                                        </FormField>

                                        <FormField
                                            label={fieldLabel(
                                                t('console.apps.createPage.ownerEmailLabel'),
                                                form.changedKeys.has('owner_email')
                                            )}
                                            error={
                                                form.ownerEmailError ?? fieldError('owner_email')
                                            }
                                        >
                                            <Input
                                                type="email"
                                                value={form.scalars.owner_email}
                                                onChange={(e) =>
                                                    form.setScalar('owner_email', e.target.value)
                                                }
                                                onBlur={() => form.markTouched('owner_email')}
                                            />
                                        </FormField>
                                    </div>
                                </SectionCard>

                                <SectionCard title={t('console.apps.createPage.oidcSectionTitle')}>
                                    <div className="portal-admin-form">
                                        <AppUriListField
                                            legend={t(
                                                'console.apps.createModal.redirectUrisLegend'
                                            )}
                                            changed={form.changedKeys.has('redirect_uris')}
                                            required
                                            values={form.redirectUris}
                                            onChange={form.setRedirectUris}
                                            onBlur={() => form.markTouched('redirect_uris')}
                                            error={
                                                form.redirectError ?? fieldError('redirect_uris')
                                            }
                                            placeholder={t(
                                                'console.apps.createModal.redirectUriPlaceholder'
                                            )}
                                            addLabel={t('console.apps.createModal.addUriButton')}
                                        />

                                        <AppUriListField
                                            legend={t(
                                                'console.apps.createModal.postLogoutUrisLegend'
                                            )}
                                            changed={form.changedKeys.has(
                                                'post_logout_redirect_uris'
                                            )}
                                            values={form.postLogoutUris}
                                            onChange={form.setPostLogoutUris}
                                            onBlur={() =>
                                                form.markTouched('post_logout_redirect_uris')
                                            }
                                            error={
                                                form.postLogoutError ??
                                                fieldError('post_logout_redirect_uris')
                                            }
                                            placeholder={t(
                                                'console.apps.createModal.webUrlPlaceholder'
                                            )}
                                            addLabel={t('console.apps.createModal.addUriButton')}
                                        />

                                        <OptionCardGroup
                                            legend={fieldLabel(
                                                t('console.apps.createModal.orgScopeLegend'),
                                                form.changedKeys.has('org_scope_mode')
                                            )}
                                            footer={
                                                <FormFieldMessage
                                                    error={fieldError('org_scope_mode')}
                                                />
                                            }
                                        >
                                            <OptionCard
                                                inputType="radio"
                                                name="org_scope_mode"
                                                selected={
                                                    form.scalars.org_scope_mode === 'selected_orgs'
                                                }
                                                onSelect={() =>
                                                    form.setScalar(
                                                        'org_scope_mode',
                                                        'selected_orgs'
                                                    )
                                                }
                                                title={t(
                                                    'console.apps.createModal.orgScopeSelectedTitle'
                                                )}
                                                description={t(
                                                    'console.apps.createModal.orgScopeSelectedDescription'
                                                )}
                                            />
                                            <OptionCard
                                                inputType="radio"
                                                name="org_scope_mode"
                                                selected={
                                                    form.scalars.org_scope_mode === 'all_orgs'
                                                }
                                                onSelect={() =>
                                                    form.setScalar('org_scope_mode', 'all_orgs')
                                                }
                                                title={t('console.apps.scope.allOrgs')}
                                                description={t(
                                                    'console.apps.createModal.orgScopeAllDescription'
                                                )}
                                            />
                                        </OptionCardGroup>

                                        <OptionCardGroup
                                            legend={fieldLabel(
                                                t('console.apps.createModal.roleManagementLegend'),
                                                form.changedKeys.has('supports_role_management')
                                            )}
                                            footer={
                                                <FormFieldMessage
                                                    error={fieldError('supports_role_management')}
                                                />
                                            }
                                        >
                                            <OptionCard
                                                inputType="checkbox"
                                                selected={form.scalars.supports_role_management}
                                                onSelect={() =>
                                                    form.setScalar(
                                                        'supports_role_management',
                                                        !form.scalars?.supports_role_management
                                                    )
                                                }
                                                title={t(
                                                    'console.apps.createModal.roleManagementTitle'
                                                )}
                                                description={
                                                    <>
                                                        {t(
                                                            'console.apps.createModal.roleManagementDescriptionPrefix'
                                                        )}
                                                        <code>POST /api/v1/management/roles</code>
                                                        {t(
                                                            'console.apps.createModal.roleManagementDescriptionSuffix'
                                                        )}
                                                    </>
                                                }
                                            />
                                        </OptionCardGroup>

                                        {form.formError && (
                                            <p className="portal-admin-error">{form.formError}</p>
                                        )}
                                    </div>
                                </SectionCard>

                                <AppEndpointPathsCard
                                    title={t('console.appDetail.edit.endpoints.sectionTitle')}
                                    keys={CHECK_ENDPOINT_KEYS}
                                    values={form.endpointPaths}
                                    errors={form.endpointPathErrors}
                                    serverError={fieldError('endpoint_paths')}
                                    isChanged={(key) => form.changedKeys.has(`endpoint_paths.${key}`)}
                                    label={fieldLabel}
                                    onChange={form.setEndpointPath}
                                />

                                <AppEndpointPathsCard
                                    title={t('console.appDetail.edit.endpoints.syncSectionTitle')}
                                    keys={SYNC_ENDPOINT_KEYS}
                                    values={form.endpointPaths}
                                    errors={form.endpointPathErrors}
                                    serverError={null}
                                    isChanged={(key) => form.changedKeys.has(`endpoint_paths.${key}`)}
                                    label={fieldLabel}
                                    onChange={form.setEndpointPath}
                                />
                            </form>

                            <aside className={styles.bodySide}>
                                <SectionCard
                                    title={
                                        form.unsavedCount > 0
                                            ? t('console.appDetail.edit.unsavedCount', {
                                                  count: form.unsavedCount,
                                              })
                                            : t('console.appDetail.edit.diffPanelTitleEmpty')
                                    }
                                >
                                    {form.diffEntries.length === 0 ? (
                                        <p className="portal-admin-hint">
                                            {t('console.appDetail.edit.diffPanelEmpty')}
                                        </p>
                                    ) : (
                                        <ul className={styles.diffList}>
                                            {form.diffEntries.map((entry) => (
                                                <li
                                                    key={entry.key}
                                                    className={styles.diffListRow}
                                                >
                                                    <span className={styles.diffListLabel}>
                                                        {t(entry.labelKey)}
                                                    </span>
                                                    <span className={styles.diffListBefore}>
                                                        {entry.before}
                                                    </span>
                                                    <span className={styles.diffListAfter}>
                                                        {entry.after}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </SectionCard>

                                <Callout tone="info">
                                    {t('console.appDetail.edit.diffNote')}
                                </Callout>
                                <Callout tone="warning">
                                    {t('console.appDetail.edit.ciTokenNote')}
                                </Callout>
                            </aside>
                        </div>
                    </div>
                </>
            )}

            <AppProvisioningResultModal
                provisioning={provisioningResult.provisioning}
                onClose={provisioningResult.close}
            />
        </main>
    );
}

export default AppEditPage;
